import React, { useEffect, useState } from 'react';
import { zoneAPI } from '../../api/client';
import { PageHeader } from '../../components/UI';
import MapPicker from '../../components/MapPicker';
import T from '../../theme';

const ZONE_PRESETS = [
  { type: 'maktab', icon: '🏫', color: '#3B82F6', label: 'Maktab', defRadius: 120 },
  { type: 'uy', icon: '🏠', color: '#2563EB', label: 'Uy', defRadius: 80 },
  { type: 'boqcha', icon: '🌸', color: '#EC4899', label: 'Boqcha', defRadius: 100 },
  { type: 'sport', icon: '⚽', color: '#F97316', label: 'Sport', defRadius: 200 },
  { type: 'madaniyat', icon: '🎨', color: '#8B5CF6', label: 'Madaniyat', defRadius: 150 },
  { type: 'masjid', icon: '🕌', color: '#14B8A6', label: 'Masjid', defRadius: 100 },
  { type: 'ish', icon: '💼', color: '#6366F1', label: 'Ish joyi', defRadius: 150 },
  { type: 'boshqa', icon: '📍', color: '#64748B', label: 'Boshqa', defRadius: 150 },
];

function getPreset(type) {
  return ZONE_PRESETS.find(p => p.type === type) || ZONE_PRESETS[ZONE_PRESETS.length - 1];
}

// Responsive hook
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < breakpoint);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, [breakpoint]);
  return isMobile;
}

// ===== ZONE CARD =====
function ZoneCard({ zone, onDelete, onEdit, isSelected, onSelect }) {
  const [h, setH] = useState(false);
  const preset = getPreset(zone.zone_type);
  const c = zone.color || preset.color;

  return (
    <div
      onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      onClick={() => onSelect?.(zone)}
      style={{
        background: isSelected ? c + '08' : '#fff',
        borderRadius: 16, padding: '16px 18px',
        boxShadow: h || isSelected ? T.shadowMd : T.shadow,
        border: `2px solid ${isSelected ? c : h ? c + '30' : T.border}`,
        transition: 'all 0.2s', cursor: 'pointer',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 12, background: c + '15',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
        }}>{zone.icon || preset.icon}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{zone.name}</div>
          <div style={{ fontSize: 12, color: c, fontWeight: 700 }}>
            {preset.label} · {zone.radius}m
          </div>
        </div>
        {zone.children_count > 0 && (
          <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 800, background: c + '15', color: c }}>
            {zone.children_count}
          </span>
        )}
      </div>

      {zone.address && (
        <div style={{ fontSize: 12, color: T.textMuted, fontWeight: 600, marginBottom: 10, lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          📍 {zone.address}
        </div>
      )}

      <div style={{ display: 'flex', gap: 6 }}>
        <button onClick={(e) => { e.stopPropagation(); onEdit(zone); }} style={{
          flex: 1, padding: '7px', background: c + '10', border: 'none', borderRadius: 8,
          fontSize: 12, fontWeight: 700, color: c, cursor: 'pointer', fontFamily: T.font,
        }}>✏️ Tahrirlash</button>
        <button onClick={(e) => { e.stopPropagation(); onDelete(zone.id); }} style={{
          padding: '7px 12px', background: '#FEF2F2', border: 'none', borderRadius: 8,
          fontSize: 12, fontWeight: 700, color: '#DC2626', cursor: 'pointer', fontFamily: T.font,
        }}>🗑</button>
      </div>
    </div>
  );
}

// ===== ZONE FORM (ADAPTIV) =====
function ZoneForm({ editData, onSave, onCancel, otherZones }) {
  const [name, setName] = useState(editData?.name || '');
  const [zoneType, setZoneType] = useState(editData?.zone_type || 'boshqa');
  const [address, setAddress] = useState(editData?.address || '');
  const [lat, setLat] = useState(editData?.latitude || null);
  const [lng, setLng] = useState(editData?.longitude || null);
  const [radius, setRadius] = useState(editData?.radius || 150);
  const [zoneIcon, setZoneIcon] = useState(editData?.icon || '');
  const [color, setColor] = useState(editData?.color || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const isMobile = useIsMobile();

  const preset = getPreset(zoneType);

  const selectPreset = (p) => {
    setZoneType(p.type);
    setZoneIcon(p.icon);
    setColor(p.color);
    setRadius(p.defRadius);
    if (!name.trim() || ZONE_PRESETS.some(pp => pp.label === name)) setName(p.label);
  };

  const handleMapChange = ({ latitude: la, longitude: lo, address: addr }) => {
    setLat(la); setLng(lo);
    if (addr) setAddress(addr);
  };

  const handleSubmit = async () => {
    if (!name.trim()) { setError('Hudud nomini kiriting'); return; }
    if (!lat || !lng) { setError('Xaritadan joy tanlang yoki qidiring'); return; }
    setLoading(true); setError('');
    try {
      await onSave({
        name, zone_type: zoneType, address,
        latitude: lat, longitude: lng, radius,
        icon: zoneIcon || preset.icon, color: color || preset.color,
      });
    } catch (err) {
      const d = err.response?.data;
      setError(typeof d === 'object' ? Object.values(d).flat().join(', ') : 'Xatolik');
    }
    setLoading(false);
  };

  const inp = {
    width: '100%', padding: '13px 16px', background: T.bg,
    border: `2px solid ${T.border}`, borderRadius: 12, fontSize: 15,
    fontFamily: T.font, fontWeight: 600, color: T.text,
    outline: 'none', boxSizing: 'border-box',
  };

  const mapOthers = otherZones.filter(z => z.id !== editData?.id);

  return (
    <div style={{
      background: '#fff', borderRadius: 20, padding: isMobile ? '20px 16px' : '28px',
      boxShadow: T.shadowMd, border: `2px solid ${(color || preset.color) + '25'}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <h3 style={{ fontSize: isMobile ? 17 : 19, fontWeight: 900, color: T.text, margin: 0 }}>
          {editData ? '✏️ Hududni tahrirlash' : '📍 Yangi hudud'}
        </h3>
        <button onClick={onCancel} style={{
          width: 36, height: 36, borderRadius: 10, background: T.bg,
          border: `1.5px solid ${T.border}`, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16,
        }}>✕</button>
      </div>

      {error && (
        <div style={{ background: '#FEF2F2', color: '#DC2626', padding: '12px 16px', borderRadius: 12, fontSize: 14, fontWeight: 700, marginBottom: 16 }}>⚠️ {error}</div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Zone type selector — 4 ta yoki 3 ta ustun */}
        <div>
          <label style={{ fontSize: 12, fontWeight: 800, color: T.textMuted, display: 'block', marginBottom: 8, letterSpacing: 0.5 }}>HUDUD TURI</label>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(3, 1fr)' : 'repeat(4, 1fr)', gap: 6 }}>
            {ZONE_PRESETS.map(p => (
              <button key={p.type} onClick={() => selectPreset(p)} style={{
                padding: isMobile ? '8px 4px' : '10px 4px', background: zoneType === p.type ? p.color + '15' : T.bg,
                border: `2px solid ${zoneType === p.type ? p.color : 'transparent'}`, borderRadius: 12,
                cursor: 'pointer', fontFamily: T.font, fontSize: isMobile ? 11 : 12, fontWeight: 700,
                color: zoneType === p.type ? p.color : T.textMuted, transition: 'all 0.15s',
                textAlign: 'center',
              }}>
                <div style={{ fontSize: isMobile ? 20 : 22, marginBottom: 3 }}>{p.icon}</div>
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Name */}
        <div>
          <label style={{ fontSize: 12, fontWeight: 800, color: T.textMuted, display: 'block', marginBottom: 6, letterSpacing: 0.5 }}>HUDUD NOMI *</label>
          <input style={inp} value={name} onChange={e => setName(e.target.value)} placeholder="Masalan: 10-maktab, Sport zali, Uyim..." />
        </div>

        {/* MAP */}
        <div>
          <label style={{ fontSize: 12, fontWeight: 800, color: T.textMuted, display: 'block', marginBottom: 8, letterSpacing: 0.5 }}>📍 XARITADAN JOY TANLANG *</label>
          <MapPicker
            latitude={lat}
            longitude={lng}
            radius={radius}
            color={color || preset.color}
            icon={zoneIcon || preset.icon}
            onChange={handleMapChange}
            onRadiusChange={setRadius}
            height={isMobile ? 280 : 360}
            otherZones={mapOthers}
          />
        </div>

        {/* Address */}
        <div>
          <label style={{ fontSize: 12, fontWeight: 800, color: T.textMuted, display: 'block', marginBottom: 6, letterSpacing: 0.5 }}>MANZIL</label>
          <input style={inp} value={address} onChange={e => setAddress(e.target.value)} placeholder="Avtomatik to'ldiriladi yoki qo'lda yozing" />
        </div>

        {/* Coord display */}
        {lat && lng && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 120, background: T.bg, borderRadius: 10, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 12, color: T.textMuted, fontWeight: 700 }}>Lat:</span>
              <span style={{ fontSize: 13, fontWeight: 800, color: T.text, fontFamily: T.mono }}>{lat.toFixed(6)}</span>
            </div>
            <div style={{ flex: 1, minWidth: 120, background: T.bg, borderRadius: 10, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 12, color: T.textMuted, fontWeight: 700 }}>Lng:</span>
              <span style={{ fontSize: 13, fontWeight: 800, color: T.text, fontFamily: T.mono }}>{lng.toFixed(6)}</span>
            </div>
          </div>
        )}

        {/* Submit */}
        <div style={{ display: 'flex', gap: 10, marginTop: 4, flexDirection: isMobile ? 'column-reverse' : 'row' }}>
          <button onClick={onCancel} style={{
            flex: 1, padding: '14px', background: T.bg, border: `2px solid ${T.border}`,
            borderRadius: 14, fontSize: 15, fontWeight: 700, cursor: 'pointer', color: T.textSec, fontFamily: T.font,
          }}>Bekor qilish</button>
          <button onClick={handleSubmit} disabled={loading} style={{
            flex: isMobile ? 1 : 2, padding: '14px',
            background: loading ? '#EFF6FF' : `linear-gradient(135deg, ${color || preset.color}, ${color || preset.color}CC)`,
            color: '#fff', border: 'none', borderRadius: 14, fontSize: 16, fontWeight: 800,
            cursor: loading ? 'default' : 'pointer', fontFamily: T.font,
            boxShadow: `0 4px 16px ${(color || preset.color)}35`,
          }}>{loading ? 'Saqlanmoqda...' : '✓ Saqlash'}</button>
        </div>
      </div>
    </div>
  );
}

// ===== ZONES MAP OVERVIEW =====
function ZonesMapOverview({ zones, selectedZone, onSelectZone }) {
  if (zones.length === 0) return null;
  return (
    <div style={{ background: '#fff', borderRadius: 18, overflow: 'hidden', boxShadow: T.shadow, border: `1px solid ${T.border}` }}>
      <MapPicker
        latitude={selectedZone?.latitude || zones[0]?.latitude}
        longitude={selectedZone?.longitude || zones[0]?.longitude}
        radius={selectedZone?.radius || 150}
        color={selectedZone?.color || '#2563EB'}
        icon={selectedZone?.icon || '📍'}
        readOnly={true}
        showSearch={false}
        height={300}
        otherZones={zones.filter(z => z.id !== selectedZone?.id)}
      />
    </div>
  );
}

// ===== MAIN PAGE =====
export default function ZonesPage() {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [selectedZone, setSelectedZone] = useState(null);
  const [filter, setFilter] = useState('all');
  const isMobile = useIsMobile();

  const loadData = async () => {
    try { setZones((await zoneAPI.list()).data); } catch {}
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  const handleSave = async (data) => {
    if (editData) await zoneAPI.update(editData.id, data);
    else await zoneAPI.create(data);
    setShowForm(false); setEditData(null); await loadData();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Haqiqatan ham o'chirmoqchimisiz?")) return;
    await zoneAPI.delete(id); setSelectedZone(null); await loadData();
  };

  const handleEdit = (zone) => { setEditData(zone); setShowForm(true); };
  const handleNew = () => { setEditData(null); setShowForm(true); };

  const typeCounts = zones.reduce((acc, z) => { acc[z.zone_type] = (acc[z.zone_type] || 0) + 1; return acc; }, {});
  const filteredZones = filter === 'all' ? zones : zones.filter(z => z.zone_type === filter);
  const activeTypes = [...new Set(zones.map(z => z.zone_type))];

  return (
    <div>
      <PageHeader title="📍 Hududlar" />
      {showForm ? (
        <div style={{ maxWidth: isMobile ? '100%' : '100%' }}>
          <ZoneForm
            editData={editData}
            onSave={handleSave}
            onCancel={() => { setShowForm(false); setEditData(null); }}
            otherZones={zones}
          />
        </div>
      ) : (
        <div style={{
          display: 'flex', gap: 24, alignItems: 'flex-start',
          flexDirection: isMobile ? 'column-reverse' : 'row',
        }}>
          {/* Left — list */}
          <div style={{ flex: 1, minWidth: 0, width: isMobile ? '100%' : 'auto' }}>
            {/* Filter + Add */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
              <button onClick={() => setFilter('all')} style={{
                padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                border: `1.5px solid ${filter === 'all' ? T.blue : T.border}`,
                background: filter === 'all' ? T.blueSoft : '#fff', color: filter === 'all' ? T.blueDark : T.textMuted,
                cursor: 'pointer', fontFamily: T.font,
              }}>Hammasi ({zones.length})</button>
              {activeTypes.map(type => {
                const p = getPreset(type);
                return (
                  <button key={type} onClick={() => setFilter(type)} style={{
                    padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                    border: `1.5px solid ${filter === type ? p.color : T.border}`,
                    background: filter === type ? p.color + '15' : '#fff',
                    color: filter === type ? p.color : T.textMuted,
                    cursor: 'pointer', fontFamily: T.font,
                  }}>{p.icon} {typeCounts[type]}</button>
                );
              })}
            </div>

            {/* Add button */}
            <button onClick={handleNew} style={{
              width: '100%', padding: '14px', marginBottom: 16,
              background: '#fff', border: `2px dashed ${T.blue}40`, borderRadius: 14,
              fontSize: 14, fontWeight: 800, color: T.blue, cursor: 'pointer', fontFamily: T.font,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, boxShadow: T.shadow,
            }}>＋ Hudud qo'shish</button>

            {/* Zone cards */}
            {loading ? (
              <div style={{ textAlign: 'center', padding: 40, color: T.textMuted }}>Yuklanmoqda...</div>
            ) : filteredZones.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '50px 20px', background: '#fff', borderRadius: 16, boxShadow: T.shadow }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>📍</div>
                <h3 style={{ fontSize: 17, fontWeight: 900, color: T.text, marginBottom: 6 }}>Hali hudud yo'q</h3>
                <p style={{ fontSize: 14, color: T.textMuted }}>Maktab, uy, boqcha — istalgan joyni qo'shing</p>
              </div>
            ) : (
              <div style={{
                display: isMobile ? 'flex' : 'flex',
                flexDirection: 'column', gap: 10,
              }}>
                {filteredZones.map(z => (
                  <ZoneCard
                    key={z.id} zone={z}
                    isSelected={selectedZone?.id === z.id}
                    onSelect={setSelectedZone}
                    onDelete={handleDelete}
                    onEdit={handleEdit}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Right — map overview */}
          {zones.length > 0 && (
            <div style={{
              width: isMobile ? '100%' : 420,
              flexShrink: 0,
              position: isMobile ? 'relative' : 'sticky',
              top: isMobile ? 0 : 88,
            }}>
              <ZonesMapOverview zones={zones} selectedZone={selectedZone} onSelectZone={setSelectedZone} />
              {selectedZone && (
                <div style={{
                  background: '#fff', borderRadius: 14, padding: '16px 18px', marginTop: 12,
                  boxShadow: T.shadow, border: `2px solid ${(selectedZone.color || T.blue) + '30'}`,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 28 }}>{selectedZone.icon}</span>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 800, color: T.text }}>{selectedZone.name}</div>
                      <div style={{ fontSize: 13, color: selectedZone.color || T.blue, fontWeight: 700 }}>
                        {getPreset(selectedZone.zone_type).label} · Radius: {selectedZone.radius}m
                      </div>
                      {selectedZone.address && <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4 }}>{selectedZone.address}</div>}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
